import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  FileCheck,
  Users,
  FileStack,
  ShieldCheck,
  Package,
  Languages,
  Send,
  Award
} from 'lucide-react';

export const RegistrationDossierPackPage = () => {
  const deliverables = [
    {
      icon: FileStack,
      title: 'Full CTD/ACTD Dossier Compilation',
      lede: "Complete Module 1 to 5 documentation, prepared to each country's specific regulatory requirements and submission format.",
      bullets: [
        'Module 1: country-specific administrative files (application forms, letters, authorizations)',
        'Module 2: Quality Overall Summary (QOS), technical overviews and summaries',
        'Module 3: quality documentation (drug substance, drug product, excipients, specifications)',
        'Module 4/5: non-clinical and clinical summaries, including bioequivalence study integration where applicable'
      ]
    },
    {
      icon: ShieldCheck,
      title: 'Manufacturing & Quality Document Alignment',
      lede: 'Every manufacturing and quality document reviewed to meet regulator expectations.',
      bullets: [
        'GMP certificate validation and readiness review',
        'Certificate of Pharmaceutical Product (CPP) verification',
        'Certificate of Analysis (CoA) and Certificate of Suitability (CoS) alignment',
        'Stability data protocol alignment for target climate zones'
      ]
    },
    {
      icon: Package,
      title: 'Label, Artwork & Packaging Files',
      lede: 'Country-compliant packaging materials designed to meet local regulatory requirements.',
      bullets: [
        'Primary and secondary label adaptations per country guidelines',
        'Artwork compliance review for African, GCC, Asian, and LATAM requirements',
        'Multi-language support (Arabic, French, Spanish, Burmese, Khmer, and more)',
        'Mock-up preparation and regulatory annotation'
      ]
    },
    {
      icon: Languages,
      title: 'Regulatory Translations',
      lede: 'Certified translations through approved partners with pharmaceutical terminology expertise.',
      bullets: [
        'Product information documents (SPC, PIL)',
        'Administrative documents and letters',
        'Technical summaries where required',
        'Label and artwork text translations'
      ]
    },
    {
      icon: Send,
      title: 'Importer Submission Kit',
      lede: 'A complete, ready-to-file package prepared specifically for your importer or distributor to submit directly to the local Ministry of Health.',
      bullets: [
        'Complete document indexing and table of contents',
        'Final submission-ready PDFs (optimized and bookmarked)',
        'Editable source files for future updates or variations',
        'Pre-drafted cover letters and application forms',
        'Administrative undertakings and authorization letters',
        'Filing checklist with country-specific requirements highlighted'
      ]
    }
  ];

  const processSteps = [
    'Gather your documents',
    'Full compilation, plus redesign of any missing modules',
    'Artwork and labels completed',
    'Ready-to-submit dossier delivered',
    'Coordination with your importer or distributor'
  ];

  const idealFor = [
    'Pharma manufacturers preparing country submissions',
    'Nutra manufacturers with complex formulations',
    'Exporters managing multiple Ministry of Health submissions',
    'Companies entering regulated markets for the first time'
  ];

  const faqs = [
    {
      q: 'What is included in CTD/ACTD dossier preparation?',
      a: "Complete Module 1 to 5 documentation prepared to each country's format: administrative files, the Quality Overall Summary, full quality documentation, and non-clinical and clinical summaries where applicable. It also includes quality document alignment, compliant artwork and labelling, certified translations, and a ready-to-file importer submission kit."
    },
    {
      q: 'Do you prepare dossiers for drug registration in Saudi Arabia and other regulated markets?',
      a: "Yes. We prepare country-specific CTD/ACTD dossiers for pharmaceutical and nutraceutical registration across Africa, GCC, Asia, and Latin America, each built to the local regulator's exact format and submission requirements, including markets such as Saudi Arabia, the UAE, Nigeria, Kenya, and across ASEAN."
    },
    {
      q: 'What is the difference between a CTD and an ACTD dossier?',
      a: 'The CTD (Common Technical Document) is the internationally harmonized format used by many regulators, while the ACTD (ASEAN Common Technical Document) is the format required across ASEAN markets. We prepare your dossier in whichever format each target country requires, so you submit correctly the first time.'
    },
    {
      q: 'Do you handle the translations needed for submission?',
      a: 'Yes. We provide certified regulatory translations through approved partners with pharmaceutical terminology expertise, covering product information (SPC, PIL), administrative documents, technical summaries, and label and artwork text, in Arabic, French, Spanish, Burmese, Khmer, and more.'
    },
    {
      q: 'Can my importer submit the dossier directly?',
      a: 'Yes. The Importer Submission Kit is a complete, ready-to-file package with indexed and bookmarked PDFs, pre-drafted cover letters, application forms, authorization letters, and a country-specific filing checklist, so your importer or distributor can submit directly to the local Ministry of Health.'
    },
    {
      q: 'Do you work with both pharma and nutraceutical products?',
      a: 'Yes. We prepare dossiers for both, and keep the tracks separate throughout, because their required documentation and submission pathways differ. This is essential for nutra manufacturers with complex formulations entering regulated markets.'
    }
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Registration & Dossier Pack',
    'alternateName': 'CTD ACTD Dossier Preparation & Drug Registration Service',
    'serviceType': 'CTD/ACTD Dossier Preparation and Pharmaceutical Registration',
    'description':
      "Complete CTD and ACTD dossier preparation across Modules 1-5, quality document alignment, compliant artwork and labelling, certified translations, and a ready-to-file importer submission kit for pharma and nutraceutical registrations across Africa, GCC, Asia and Latin America.",
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America', 'ASEAN'],
    'url': 'https://www.theapostrophe.com/success-packs/registration-dossier-pack',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/success-packs/registration-dossier-pack'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Registration & Dossier Pack works',
    'description': 'A five-step process to build a complete, submission-ready CTD/ACTD dossier for pharmaceutical or nutraceutical registration.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Success Packs', 'item': 'https://www.theapostrophe.com/success-packs' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Registration & Dossier Pack', 'item': 'https://www.theapostrophe.com/success-packs/registration-dossier-pack' }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>CTD ACTD Dossier Preparation & Drug Registration Service</title>
        <meta
          name="description"
          content="Complete CTD and ACTD dossier preparation, built, validated and ready for MoH submission. Drug and nutraceutical registration support for Africa, GCC and Asia."
        />
        <meta
          name="keywords"
          content="CTD ACTD dossier preparation, dossier compilation service, pharmaceutical product registration, drug registration Saudi Arabia, MoH submission dossier, regulatory translations pharma, label artwork compliance, importer submission kit, ASEAN Common Technical Document, nutraceutical dossier"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/success-packs/registration-dossier-pack" />

        <meta property="og:title" content="CTD ACTD Dossier Preparation & Drug Registration Service" />
        <meta property="og:description" content="Complete CTD and ACTD dossier preparation, built, validated and ready for MoH submission across Africa, GCC, Asia and LATAM." />
        <meta property="og:url" content="https://www.theapostrophe.com/success-packs/registration-dossier-pack" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CTD ACTD Dossier Preparation & Drug Registration Service" />
        <meta name="twitter:description" content="Submission-ready CTD/ACTD dossiers, translations, artwork compliance and importer submission kits for pharma & nutra." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#A060B0', color: 'white' }} data-testid="dossier-pack-badge">
            Success Pack 4
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <FileCheck className="h-12 w-12" style={{ color: '#A060B0' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="dossier-pack-h1"
          >
            Complete CTD and ACTD Dossier Preparation, Submission-Ready
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#A060B0' }}>
            Your complete dossier, built, validated, and ready for importer or Ministry of Health submission.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            Companies lose months trying to combine documents, clean data, rewrite modules, and comply with divergent country formats. The Registration &amp; Dossier Pack delivers all of it in a single, submission-ready package. We prepare your complete CTD/ACTD dossier to each country&apos;s exact format, align your quality documents, make your artwork and labelling compliant, handle certified translations, and hand your importer a ready-to-file submission kit. It is the most comprehensive documentation service for pharma and nutraceutical registrations across Africa, GCC, Asia, and Latin America.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
                data-testid="dossier-start-btn"
              >
                Start My Dossier
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#A060B0', color: '#A060B0' }}
                data-testid="dossier-proposal-btn"
              >
                Request a Proposal
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What It Solves */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" style={{ color: '#303040' }}>
            What It Solves
          </h2>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: '#606070' }}>
            Building a dossier in-house means chasing documents, reformatting for every country, and hoping nothing was missed. This pack replaces that with a clean, complete dossier built to global CTD expectations, so you eliminate internal paperwork delays and file with confidence.
          </p>
        </div>
      </section>

      {/* What You Get - Deliverables */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What You Get
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Five deliverables that turn scattered inputs into one clean, filing-ready submission.
          </p>
          <div className="space-y-10">
            {deliverables.map((d, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#A060B0' }}
                    >
                      <d.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#A060B0', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {d.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
                      {d.lede}
                    </p>
                    <div className="space-y-2">
                      {d.bullets.map((b, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#A060B0' }} aria-hidden="true" />
                          <span className="text-base" style={{ color: '#505060' }}>
                            {b}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Process
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#A060B0' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#A060B0' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#A060B0' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(160, 96, 176, 0.12)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#A060B0' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#A060B0' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              This is your fastest route to submission. You eliminate internal paperwork delays and receive a clean, complete dossier built to global CTD expectations, ready for your importer to file. A submission-ready dossier is the difference between clearing in months and waiting a year.
            </p>
          </div>
        </div>
      </section>

      {/* Retainer CTA Block */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-8 md:p-12 shadow-xl" style={{ backgroundColor: 'white' }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#A060B0' }}
              >
                <Users className="h-10 w-10 text-white" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#303040' }}>
                  Want your entire regulatory workflow handled continuously?
                </h2>
                <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#708080' }}>
                  A dossier gets you to submission. The <strong>Apostrophe Retainer</strong> keeps you there, with one dedicated regulatory affairs expert managing every stage on an ongoing basis, so you never have to hire, train, or run an in-house regulatory team.
                </p>
                <Link to="/retainer">
                  <Button
                    size="lg"
                    className="px-6 py-5 rounded-full transition-all duration-300 hover:scale-105"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    data-testid="dossier-retainer-btn"
                  >
                    Explore the Retainer Plan
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.06)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to Build Your Complete Dossier?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Share your product and target markets, and we will build a submission-ready CTD/ACTD dossier for each one.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
                data-testid="dossier-cta-start-btn"
              >
                Start My Registration Dossier
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#A060B0', color: '#A060B0' }}
                data-testid="dossier-cta-meeting-btn"
              >
                Schedule a Meeting
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about CTD / ACTD preparation, translations, and Ministry of Health submissions.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`dossier-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
